/**
 * Webhook 通知服务
 * 负责在任务变更时通知 Web UI
 */

import fetch from 'node-fetch';
import { Task } from '../types';
import { logger } from '../utils/logger';
import { getOfflineQueue } from './offline-queue';

export interface WebhookEvent {
  event: string;
  data: any;
  timestamp: number;
  taskId?: string;
  operation?: 'create' | 'update' | 'delete' | 'bulk';
}

export interface SyncStrategy {
  delay: number;     // 延迟时间（毫秒）
  batch: boolean;    // 是否批量处理
}

export class WebhookService {
  private static instance: WebhookService;
  private webhookUrl: string = 'http://localhost:3001/api/v1/webhook/sync';
  private isEnabled: boolean = true;
  private syncQueue: Map<string, NodeJS.Timeout> = new Map();
  private batchQueue: WebhookEvent[] = [];
  private batchTimer: NodeJS.Timeout | null = null;
  private offlineQueue = getOfflineQueue();

  // 同步策略配置
  private syncStrategies: Record<string, SyncStrategy> = {
    'task.create': { delay: 0, batch: false },
    'task.delete': { delay: 0, batch: false },
    'task.status': { delay: 1000, batch: false },
    'task.priority': { delay: 1000, batch: false },
    'task.update': { delay: 3000, batch: false },
    'task.edit': { delay: 3000, batch: false },
    'task.bulk': { delay: 5000, batch: true },
    'task.reorder': { delay: 5000, batch: true }
  };

  private constructor() {
    // 检查环境变量
    if (process.env.WORKFLOW_WEBHOOK_URL) {
      this.webhookUrl = process.env.WORKFLOW_WEBHOOK_URL;
    }
    if (process.env.WORKFLOW_WEBHOOK_ENABLED === 'false') {
      this.isEnabled = false;
    }
    
    // 监听离线队列事件
    this.offlineQueue.on('network-status-changed', (isOnline) => {
      logger.info(`Network status changed: ${isOnline ? 'online' : 'offline'}`);
    });
    
    this.offlineQueue.on('operation-succeeded', (op) => {
      logger.info(`Offline operation succeeded: ${op.operation}`);
    });
    
    this.offlineQueue.on('operation-failed', (op, error) => {
      logger.error(`Offline operation failed after retries: ${op.operation}`, error);
    });
  }

  static getInstance(): WebhookService {
    if (!WebhookService.instance) {
      WebhookService.instance = new WebhookService();
    }
    return WebhookService.instance;
  }

  /**
   * 发送同步通知
   */
  async notify(event: string, data: any, options?: { taskId?: string }): Promise<void> {
    if (!this.isEnabled) return;

    const webhookEvent: WebhookEvent = {
      event,
      data,
      timestamp: Date.now(),
      taskId: options?.taskId
    };

    const strategy = this.getSyncStrategy(event);

    if (strategy.batch) {
      this.queueBatchSync(webhookEvent);
    } else if (strategy.delay > 0) {
      this.queueDelayedSync(event, webhookEvent, strategy.delay);
    } else {
      await this.sendWebhook(webhookEvent);
    }
  }

  /**
   * 获取同步策略
   */
  private getSyncStrategy(event: string): SyncStrategy {
    return this.syncStrategies[event] || { delay: 0, batch: false };
  }

  /**
   * 延迟同步（防抖）
   */
  private queueDelayedSync(key: string, event: WebhookEvent, delay: number): void {
    // 取消之前的定时器
    if (this.syncQueue.has(key)) {
      clearTimeout(this.syncQueue.get(key)!);
    }

    // 设置新的定时器
    const timer = setTimeout(async () => {
      await this.sendWebhook(event);
      this.syncQueue.delete(key);
    }, delay);

    this.syncQueue.set(key, timer);
  }

  /**
   * 批量同步（累积）
   */
  private queueBatchSync(event: WebhookEvent): void {
    this.batchQueue.push(event);

    // 如果没有批量定时器，创建一个
    if (!this.batchTimer) {
      this.batchTimer = setTimeout(async () => {
        await this.flushBatchQueue();
      }, 5000);
    }
  }

  /**
   * 刷新批量队列
   */
  private async flushBatchQueue(): Promise<void> {
    if (this.batchQueue.length === 0) return;

    const events = [...this.batchQueue];
    this.batchQueue = [];
    this.batchTimer = null;

    // 发送批量事件
    await this.sendWebhook({
      event: 'batch.sync',
      data: { events },
      timestamp: Date.now()
    });
  }

  /**
   * 发送 webhook
   */
  async sendWebhook(event: WebhookEvent): Promise<void> {
    try {
      // 使用 AbortController 实现超时
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(this.webhookUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Workflow-CLI': '1.0'
        },
        body: JSON.stringify(event),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        logger.debug(`Webhook failed: ${response.status} ${response.statusText}`);
        
        // 如果是网络错误，加入离线队列
        if (response.status >= 500 || response.status === 0) {
          await this.offlineQueue.enqueue('webhook', 'send', event);
        }
      }
    } catch (error: any) {
      // 网络错误，加入离线队列
      logger.debug('Webhook notification failed:', error);
      
      if (error.code === 'ECONNREFUSED' || error.code === 'ETIMEDOUT' || error.code === 'ENOTFOUND') {
        await this.offlineQueue.enqueue('webhook', 'send', event);
      }
    }
  }

  /**
   * 任务创建通知
   */
  async notifyTaskCreated(task: Task): Promise<void> {
    await this.notify('task.create', { task }, { taskId: task.id });
  }

  /**
   * 任务更新通知
   */
  async notifyTaskUpdated(task: Task, updates: Partial<Task>): Promise<void> {
    // 根据更新内容决定事件类型
    let event = 'task.update';
    if (updates.status !== undefined) {
      event = 'task.status';
    } else if (updates.priority !== undefined) {
      event = 'task.priority';
    } else if (updates.content !== undefined) {
      event = 'task.edit';
    }

    await this.notify(event, { task, updates }, { taskId: task.id });
  }

  /**
   * 任务删除通知
   */
  async notifyTaskDeleted(taskId: string): Promise<void> {
    await this.notify('task.delete', { taskId }, { taskId });
  }

  /**
   * 批量操作通知
   */
  async notifyBulkOperation(operation: string, taskIds: string[], data?: any): Promise<void> {
    await this.notify('task.bulk', { 
      operation, 
      taskIds, 
      count: taskIds.length,
      ...data 
    });
  }

  /**
   * 重新排序通知
   */
  async notifyTasksReordered(taskIds: string[]): Promise<void> {
    await this.notify('task.reorder', { taskIds });
  }

  /**
   * 设置 webhook URL
   */
  setWebhookUrl(url: string): void {
    this.webhookUrl = url;
  }

  /**
   * 启用/禁用 webhook
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * 获取离线队列状态
   */
  getOfflineQueueStatus() {
    return this.offlineQueue.getStatus();
  }

  /**
   * 清理资源
   */
  cleanup(): void {
    // 清理所有定时器
    this.syncQueue.forEach(timer => clearTimeout(timer));
    this.syncQueue.clear();

    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    // 刷新批量队列
    this.flushBatchQueue();
    
    // 清理离线队列
    this.offlineQueue.cleanup();
  }
}

// 导出单例
export const webhookService = WebhookService.getInstance();